'use client'

import * as React from 'react'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { UserCard, UserCardSkeleton } from '../user-card'
import type { PublicUserProfile } from 'codegen/types'
import { IconPersonPin } from '@repo/design-system-cf/components/icons'

type PublicUserProfileWithoutLocation = Omit<PublicUserProfile, 'location'>

interface UserListSectionProps {
  users: PublicUserProfileWithoutLocation[]
  hasMore: boolean
  isFetchingMore: boolean
  isLoading: boolean
  fetchMore: () => void
  emptyMessage: string
  ownUserId?: string
  slug?: string
}

export function UserListSection({
  users,
  hasMore,
  isFetchingMore,
  isLoading,
  fetchMore,
  emptyMessage,
  ownUserId,
  slug,
}: UserListSectionProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)

  const tryLoadMore = React.useCallback(async () => {
    const el = scrollRef.current
    if (!el) {
      return
    }

    const distance = el.scrollHeight - el.scrollTop - el.clientHeight

    const shouldFetch = (hasMore ?? true) && !isFetchingMore
    if (!shouldFetch) {
      return
    }

    if (users.length > 0 && distance > 60) {
      return
    }

    await fetchMore()
  }, [hasMore, isFetchingMore, fetchMore, users.length])

  const handleScroll = React.useCallback(() => {
    void tryLoadMore()
  }, [tryLoadMore])

  const isInitialLoading = isLoading && users.length === 0

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void tryLoadMore()
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [tryLoadMore])

  return (
    <div ref={scrollRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto">
      <div className="grid xl:grid-cols-5 lg:grid-cols-4 sm:grid-cols-3 gap-4 items-start">
        {isInitialLoading
          ? Array.from({ length: 8 }).map((_, i) => <UserCardSkeleton key={i} />)
          : users.map((u, i) => (
              <UserCard key={`${u.id}-${i}`} user={u} ownUserID={ownUserId} slug={slug} />
            ))}
        {(hasMore ?? true) && !isInitialLoading ? (
          <div className="pb-4 col-span-full flex items-center justify-center">
            {isFetchingMore ? <Spinner /> : null}
          </div>
        ) : null}
      </div>
      {!isLoading && users.length === 0 && hasMore === false ? (
        <div className="w-full">
          <div className="bg-cf-white border border-cf-transparent-grey-1000-12 rounded-[12px] p-6 w-full flex flex-col items-center justify-start">
            <IconPersonPin className="size-10 text-cf-purple-900" />
            <p className="text-20 font-bold text-center text-cf-grey-1000 my-4">{emptyMessage}</p>
          </div>
        </div>
      ) : null}
    </div>
  )
}
